import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { theme } from "../../../themes/theme";
import { Acknowledgment } from "../../../types/models/Acknowledgement";
import { Fonts } from "../../../constants/AppEnums";
import { AppState } from "../../../redux/store";
import { RuleUnitState } from "../../../types/models/RuleUnitState";
import { TeamMember, TeamMemberProgress } from "../../../types/models/TeamMember";
import { getRuleInstanceMetadata } from "../../../utils/RuleUtil";
import AppDialog from "../../../components/AppBase/AppDialog";
import MemberAlert from "./MemberAlert";
import Chat from "../../../components/Chat";
import EmptyResult from "../../rules/EmptyResult";
import { VirtualTeam } from "../../../types/models/Team";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { RoleState } from "../../../types/models/RoleState";
import "../MemberTable/MemberTableStyle.scss";
import { AlertMetaDataInstance } from "../../../types/models/AlertMetaData";
import { displayUserRole } from "../../../utils/StringUtils";
import ProgressPieChart from "../../../components/TableItems/ProgressPieChart";
import { TeamMembersState } from "../../../types/models/TeamMembersState";
import {
  getMyProgressDetail,
  getMyTrainingDetail,
  getProgressOfUsers,
  getTrainingOfUser,
} from "../../../redux/actions";
import { UserFitnessStatus } from "../../../types/models/UserFitnessStatus";
import { UserSkill } from "../../../types/models/UserSkillCoverage";
import { UserSkillItem } from "../../../types/models/UserSkillItem";
import { Message, QuestionMark } from "@mui/icons-material";
import AppScrollbar from "../../../components/AppBase/AppScrollbar";
import { getFitForWorkIcon } from "../../../utils/FitForWorkUtil";
import { fontColor } from "../../../constants/ColorSets";
import { UserTrainingList } from "./UserTrainingList";
import { FitForWorkDetail } from "./FitForWork";

interface MemberModalProps {
  open: boolean;
  onClose: () => void;
  user: TeamMember;
  userProgress: Acknowledgment[];
  selectedMember: TeamMemberProgress | undefined;
  setSelectedMember?: (selectedMember: TeamMemberProgress) => void;
  currentTeam: VirtualTeam | undefined;
  alerts: AlertMetaDataInstance;
  userFitness?: UserFitnessStatus;
  userTraining?: UserSkill;
  userProfilePage?: boolean;
}

const MemberModal = ({
  open,
  onClose,
  user,
  selectedMember,
  setSelectedMember,
  currentTeam,
  alerts,
  userFitness,
  userTraining,
  userProfilePage,
}: MemberModalProps) => {
  const { user_id, rule_id } = useParams();
  const dispatch = useDispatch();

  const ruleUnitState = useSelector<AppState, RuleUnitState>((state) => state.ruleUnit);
  const roleState = useSelector<AppState, RoleState>((state) => state.role);
  const teamState = useSelector<AppState, TeamMembersState>((state) => state.teamMembers);
  const alertDetailMap = new Map(
    ruleUnitState.baseAlertRules
      ?.concat(ruleUnitState.baseAlertHistories ?? [])
      .map((rule) => [rule.RuleInstanceId, rule])
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [openChatting, setOpenChatting] = useState<boolean>(false);
  const [selectedRuleInstanceId, setSelectedRuleInstanceId] = useState<string | undefined>();
  const [resolvedValue, setResolvedValue] = useState<number>(0);
  const [selectedType, setSelectedType] = useState<string>("Alert");
  const [userTrainingList, setUserTrainingList] = useState<UserSkillItem[]>();
  const [userProgressList, setUserProgressList] = useState<Acknowledgment[]>();

  const contentHeight = "calc(66vh - 260px)";

  const handleResolvedChange = (event: React.SyntheticEvent, newValue: number) => {
    setResolvedValue(newValue);
  };

  const removeDuplicateAlerts = (alerts: Acknowledgment[]) => {
    const uniqueRuleIds = new Set<string>();
    return alerts.filter((alert) => {
      if (uniqueRuleIds.has(alert.RuleInstanceId)) {
        return false;
      }
      uniqueRuleIds.add(alert.RuleInstanceId);
      return true;
    });
  };

  const getUnreadAlert = (progress: Acknowledgment) => {
    if (!progress) {
      return null;
    }

    if (!progress.Resolved) {
      if (progress.SenderId !== null) {
        if (progress.SenderId === progress.UserId) {
          return true;
        }
      } else if (progress.SenderId === null && progress.DisplayMessageCount === 1) {
        return true;
      }
    } else {
      return false;
    }
  };

  const getSortedUserProgress = useCallback(() => {
    const sortedUniqueAlerts = removeDuplicateAlerts(teamState.progressOfUsers ?? []).sort(
      (a, b) => {
        const detailA = alertDetailMap.get(a.RuleInstanceId);
        const detailB = alertDetailMap.get(b.RuleInstanceId);
        if (detailA && detailB) {
          const prefixA = getRuleInstanceMetadata(detailA.Metadata ?? "")?.prefix;
          const prefixB = getRuleInstanceMetadata(detailB.Metadata ?? "")?.prefix;
          if (detailA.CreatedAt !== null && detailB.CreatedAt !== null) {
            return (
              new Date(detailB.CreatedAt ?? "").getTime() -
              new Date(detailA.CreatedAt ?? "").getTime()
            );
          }
          if (prefixA && prefixB) {
            if (prefixA[0] === prefixB[0])
              return detailA.DisplayName > detailB.DisplayName ? 1 : -1;
            else return Number.parseInt(prefixA[0]) < Number.parseInt(prefixB[0]) ? -1 : 1;
          } else {
            return detailA.DisplayName > detailB.DisplayName ? 1 : -1;
          }
        }
        return 0;
      }
    );

    const unreadAlerts: Acknowledgment[] = [];
    const readAlerts: Acknowledgment[] = [];

    sortedUniqueAlerts.filter((alert) => {
      if (getUnreadAlert(alert)) {
        unreadAlerts.push(alert);
      } else {
        readAlerts.push(alert);
      }
    });

    return [...unreadAlerts, ...readAlerts];
  }, [userProgressList, alertDetailMap]);

  const getLocationName = (locationId: string) => {
    const knownLocations = roleState.knownRoles?.locations;

    const location = knownLocations?.find((location) => location.LNIId === locationId);

    return location ? `${location?.LNIId} - ${location?.Name}` : locationId;
  };

  const createRuleProgressPieChart = (member: TeamMemberProgress | undefined) => {
    if (!member?.alertCount) {
      // Unknown person who is in Team but not in Organisation
      return (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Box
            sx={{
              minWidth: "120px",
              minHeight: "120px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <QuestionMark sx={{ mt: 0, height: "32px", width: "46px", color: "lightgray" }} />
          </Box>
        </Box>
      );
    } else {
      return (
        <Box sx={{ position: "relative", ml: 1 }}>
          <ProgressPieChart
            total={member.alertCount}
            highlighted={member.overdueCount}
            completed={member.acknowledgedCount}
            modal
          />
          {member.alertCount > 0 ? (
            <Box
              sx={{
                position: "absolute",
                ml: 6,
                top: 52,
                width: 12,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Typography variant="h4" sx={{ color: "#747474" }}>
                {member.alertCount - member.acknowledgedCount}
              </Typography>
            </Box>
          ) : null}
        </Box>
      );
    }
  };

  useEffect(() => {
    // Open chatting window if the rule instance id is specified in URL
    if (user_id && rule_id && user_id == user.user_id) {
      setSelectedRuleInstanceId(rule_id);
      setOpenChatting(true);
    }
  }, [user]);

  useEffect(() => {
    setSelectedType("Alert");
    if (userProfilePage) {
      setLoading(true);
      dispatch(
        getMyProgressDetail((progress) => {
          setUserProgressList(progress);
          dispatch(
            getMyTrainingDetail((trainingList) => {
              setUserTrainingList(trainingList);
              setLoading(false);
            })
          );
        })
      );
    } else {
      if (selectedMember?.user_id) {
        setLoading(true);
        dispatch(
          getProgressOfUsers([selectedMember?.user_id], (progress) => {
            setUserProgressList(progress);
            dispatch(
              getTrainingOfUser(selectedMember?.user_id ?? "", (trainingList) => {
                if (trainingList) {
                  setUserTrainingList(trainingList);
                }

                setLoading(false);
              })
            );
          })
        );
      } else {
        setUserTrainingList(undefined);
        setUserProgressList(undefined);
      }
    }
  }, [selectedMember]);

  const alertDetail = () => {
    if (!userProgressList || userProgressList.length === 0) {
      return (
        <Box sx={{ py: 4, borderTop: "1px solid lightgray" }}>
          <EmptyResult>There are currently no alerts assigned</EmptyResult>
        </Box>
      );
    } else {
      return (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Tabs value={resolvedValue} onChange={handleResolvedChange}>
              <Tab sx={{ textTransform: "none" }} label="Unresolved" />
              <Tab sx={{ textTransform: "none" }} label="Acknowledged" />
            </Tabs>
          </Box>
          {userProgressList.filter((progress) => progress.Resolved === (resolvedValue === 1))
            .length > 0 ? (
            <AppScrollbar
              autoHide={false}
              sx={{
                maxHeight: contentHeight,
                pt: 0.5,
              }}
            >
              {getSortedUserProgress()
                .filter((progress) => progress.Resolved === (resolvedValue === 1))
                .map((progress) => (
                  <MemberAlert
                    key={progress.RuleInstanceId}
                    progress={progress}
                    ruleInstance={alertDetailMap.get(progress.RuleInstanceId)}
                    openAlertChat={(ruleInstanceId) => {
                      setSelectedRuleInstanceId(ruleInstanceId);
                      setOpenChatting(true);
                    }}
                    alerts={alerts}
                    getUnreadAlert={getUnreadAlert}
                    hideChatIcon={userProfilePage}
                  />
                ))}
            </AppScrollbar>
          ) : (
            <EmptyResult>
              {resolvedValue === 0
                ? "There are currently no unresolved alerts"
                : "There are currently no acknowledged alerts"}
            </EmptyResult>
          )}
        </Box>
      );
    }
  };

  const createTrainingPieChart = () => {
    if (userTraining && userTraining.Total !== null && userTraining.Total !== 0) {
      return (
        <Box sx={{ ml: 1 }}>
          <ProgressPieChart
            completed={userTraining.Valid ?? 0}
            total={userTraining.Total ?? 0}
            highlighted={(userTraining.Total ?? 0) - (userTraining.Valid ?? 0)}
            modal
          ></ProgressPieChart>
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            minWidth: "120px",
            minHeight: "120px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <QuestionMark sx={{ color: "lightgray", width: "32px", height: "46px" }} />
        </Box>
      );
    }
  };

  const createFitForWorkIcon = () => {
    if (userFitness && userFitness.StatusKey !== null) {
      const icon = getFitForWorkIcon(userFitness.StatusKey, "96px");
      return (
        <Box
          sx={{
            minWidth: "120px",
            minHeight: "120px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {icon}
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            minWidth: "120px",
            minHeight: "120px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <QuestionMark sx={{ color: "lightgray", width: "32px", height: "46px" }} />
        </Box>
      );
    }
  };

  return (
    <>
      <AppDialog
        open={open}
        onClose={onClose}
        dividers
        fullHeight
        paperProps={{ sx: { background: fontColor.grayBackground } }}
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <Typography sx={{ fontWeight: Fonts.SEMI_BOLD, fontSize: "24" }}>
                {user.name}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  background: fontColor.grayBackground,
                  color: theme.palette.grey[600],
                }}
              >
                {user.email}
              </Typography>
              {user.roleBundles && user.roleBundles.length > 0 ? (
                <Accordion
                  elevation={0}
                  sx={{
                    m: 0,
                    p: 0,
                    width: "100%",
                    boxShadow: "none",
                    borderRadius: "0px",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    background: fontColor.grayBackground,
                    color: theme.palette.grey[600],
                  }}
                >
                  <AccordionSummary sx={{ m: 0, p: 0 }} expandIcon={<ExpandMoreIcon />}>
                    Role Bundles
                  </AccordionSummary>
                  <AccordionDetails className="accordion-values" sx={{ m: 0, p: 0 }}>
                    {user.roleBundles?.map((bundle) => (
                      <Tooltip key={bundle.id} title={bundle.name} placement="top">
                        <Chip sx={{ m: 0.5 }} label={bundle.name} size="small" />
                      </Tooltip>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    color: theme.palette.grey[600],
                  }}
                >
                  User has no role bundles assigned
                </Typography>
              )}
              {user.roles !== null && user.roles.length > 0 ? (
                <Accordion
                  elevation={0}
                  sx={{
                    m: 0,
                    p: 0,
                    width: "100%",
                    boxShadow: "none",
                    borderRadius: "0px",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    background: fontColor.grayBackground,
                    color: theme.palette.grey[600],
                  }}
                >
                  <AccordionSummary sx={{ m: 0, p: 0 }} expandIcon={<ExpandMoreIcon />}>
                    Roles
                  </AccordionSummary>
                  <AccordionDetails className="accordion-values" sx={{ m: 0, p: 0 }}>
                    {user.roles?.map((role) => (
                      <Tooltip
                        key={role}
                        title={displayUserRole(role, roleState.knownRoles?.roles)}
                        placement="top"
                      >
                        <Chip
                          sx={{ m: 0.5 }}
                          label={displayUserRole(role, roleState.knownRoles?.roles)}
                          size="small"
                        />
                      </Tooltip>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    color: theme.palette.grey[600],
                  }}
                >
                  User has no roles assigned
                </Typography>
              )}

              {user.locations !== null && user.locations.length > 0 ? (
                <Accordion
                  elevation={0}
                  sx={{
                    m: 0,
                    p: 0,
                    width: "100%",
                    boxShadow: "none",
                    borderRadius: "0px",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    background: fontColor.grayBackground,
                    color: theme.palette.grey[600],
                  }}
                >
                  <AccordionSummary sx={{ m: 0, p: 0 }} expandIcon={<ExpandMoreIcon />}>
                    Locations
                  </AccordionSummary>
                  <AccordionDetails className="accordion-values-location" sx={{ m: 0, p: 0 }}>
                    {user.locations?.map((location) => (
                      <Tooltip
                        key={location}
                        title={getLocationName(location ?? "")}
                        placement="top"
                      >
                        <Chip
                          sx={{ m: 0.5, p: 1 }}
                          label={getLocationName(location ?? "")}
                          size="small"
                        />
                      </Tooltip>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    color: theme.palette.grey[600],
                  }}
                >
                  User has no locations assigned
                </Typography>
              )}
            </Box>
          </Box>
        }
      >
        <Card>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Tabs value={selectedType} onChange={(event, newValue) => setSelectedType(newValue)}>
              <Tab
                value="Alert"
                icon={createRuleProgressPieChart(selectedMember)}
                iconPosition="top"
                label="Alerts"
                sx={{
                  textTransform: "none",
                  "&.Mui-selected": {
                    color: fontColor.titleFontColor,
                    backgroundColor: fontColor.grayTabBackground,
                  },
                }}
              />
              <Tab
                value="Training"
                icon={createTrainingPieChart()}
                iconPosition="top"
                label="Training"
                sx={{
                  textTransform: "none",
                  "&.Mui-selected": {
                    color: fontColor.titleFontColor,
                    backgroundColor: fontColor.grayTabBackground,
                  },
                }}
              />
              <Tab
                value="FitForWork"
                icon={createFitForWorkIcon()}
                iconPosition="top"
                label="Fitness"
                sx={{
                  textTransform: "none",
                  "&.Mui-selected": {
                    color: fontColor.titleFontColor,
                    backgroundColor: fontColor.grayTabBackground,
                  },
                }}
              />
            </Tabs>
          </Box>
          <Box
            sx={{
              mt: 2,
              overflowY: "auto",
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "150px",
                }}
              >
                <CircularProgress size="3rem" sx={{ color: fontColor.orangeTitle }} />
              </Box>
            ) : (
              <>
                {selectedType === "Alert" && alertDetail()}
                {selectedType === "FitForWork" && <FitForWorkDetail userFitness={userFitness} />}
                {selectedType === "Training" && (
                  <UserTrainingList trainingList={userTrainingList ?? []} />
                )}
              </>
            )}
          </Box>
        </Card>
      </AppDialog>
      {selectedRuleInstanceId && (
        <AppDialog
          open={openChatting}
          dividers
          onClose={() => {
            setOpenChatting(false);
          }}
          title={
            <Box sx={{ display: "flex", width: "100%" }}>
              <Message sx={{ mr: 1, mt: "2px", pl: 0 }} />
              <Typography
                component="h6"
                sx={{
                  mb: 1,
                  fontWeight: Fonts.SEMI_BOLD,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user.name}
              </Typography>
              <Typography
                sx={{
                  mb: 1,
                  ml: 1,
                  fontWeight: Fonts.REGULAR,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {alertDetailMap.get(selectedRuleInstanceId)?.DisplayName}
              </Typography>
            </Box>
          }
        >
          <Chat
            ruleInstanceId={selectedRuleInstanceId}
            subordinateId={user.user_id ?? ""}
            responseMode={ruleUnitState.userShieldId !== user.user_id} // If the login user is selected, open with sending mode
            userProgress={userProgressList}
            selectedMember={selectedMember}
            setSelectedMember={setSelectedMember}
            enableEscalation={true}
            currentTeam={currentTeam}
          />
        </AppDialog>
      )}
    </>
  );
};
export default MemberModal;
